import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import type { CartLine } from '../types'

const STORAGE_KEY = 'hc_cart_v1'

/** A line is unique per product + chosen color + chosen size. */
export function lineKey(l: Pick<CartLine, 'product_id' | 'color' | 'size'>): string {
  return `${l.product_id}|${l.color ?? ''}|${l.size ?? ''}`
}

interface CartContextValue {
  lines: CartLine[]
  count: number
  subtotal: number
  isOpen: boolean
  addItem: (line: Omit<CartLine, 'qty'>, qty?: number) => void
  setQty: (key: string, qty: number) => void
  removeItem: (key: string) => void
  clear: () => void
  openCart: () => void
  closeCart: () => void
}

const CartContext = createContext<CartContextValue | null>(null)

function load(): CartLine[] {
  if (typeof window === 'undefined') return []
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    return raw ? (JSON.parse(raw) as CartLine[]) : []
  } catch {
    return []
  }
}

export function CartProvider({ children }: { children: ReactNode }) {
  const [lines, setLines] = useState<CartLine[]>(load)
  const [isOpen, setIsOpen] = useState(false)

  useEffect(() => {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(lines))
  }, [lines])

  const addItem = useCallback((line: Omit<CartLine, 'qty'>, qty = 1) => {
    const key = lineKey(line)
    setLines((prev) => {
      const existing = prev.find((l) => lineKey(l) === key)
      if (existing) {
        return prev.map((l) => (lineKey(l) === key ? { ...l, qty: l.qty + qty } : l))
      }
      return [...prev, { ...line, qty }]
    })
    setIsOpen(true)
  }, [])

  const setQty = useCallback((key: string, qty: number) => {
    setLines((prev) =>
      qty <= 0
        ? prev.filter((l) => lineKey(l) !== key)
        : prev.map((l) => (lineKey(l) === key ? { ...l, qty } : l))
    )
  }, [])

  const removeItem = useCallback((key: string) => {
    setLines((prev) => prev.filter((l) => lineKey(l) !== key))
  }, [])

  const clear = useCallback(() => setLines([]), [])

  const value = useMemo<CartContextValue>(() => {
    const count = lines.reduce((sum, l) => sum + l.qty, 0)
    const subtotal = lines.reduce((sum, l) => sum + l.price * l.qty, 0)
    return {
      lines,
      count,
      subtotal,
      isOpen,
      addItem,
      setQty,
      removeItem,
      clear,
      openCart: () => setIsOpen(true),
      closeCart: () => setIsOpen(false),
    }
  }, [lines, isOpen, addItem, setQty, removeItem, clear])

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>
}

export function useCart(): CartContextValue {
  const ctx = useContext(CartContext)
  if (!ctx) throw new Error('useCart must be used within a CartProvider')
  return ctx
}
